import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ListingsService, UserListing } from '../services/listings.service';
import { AuthService } from '../services/auth.service';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';

interface ExtendedUserListing extends UserListing {
  images: string[];
  currentImageIndex: number;
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

interface PaginatedUserListings {
  vehicles: ExtendedUserListing[];
  realEstate: ExtendedUserListing[];
  marketplace: ExtendedUserListing[];
  jobs: ExtendedUserListing[];
  total: number;
  pagination: {
    vehicles: { page: number; limit: number; hasMore: boolean };
    realEstate: { page: number; limit: number; hasMore: boolean };
    marketplace: { page: number; limit: number; hasMore: boolean };
    jobs: { page: number; limit: number; hasMore: boolean };
  };
}

interface AllListingsPaginated {
  listings: ExtendedUserListing[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface AllUserListings {
  vehicles: UserListing[];
  realEstate: UserListing[];
  marketplace: UserListing[];
  jobs: UserListing[];
  total: number;
}

@Component({
  selector: 'app-my-listings',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './my-listings.component.html',
  styleUrl: './my-listings.component.css',
})
export class MyListingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private refreshSubject = new Subject<void>();

  allListingsFlat: ExtendedUserListing[] = [];
  loadedImages: Set<string> = new Set();

  private readonly SWIPE_THRESHOLD = 30;
  private readonly SWIPE_FAST_THRESHOLD = 20;
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3;
  private readonly SWIPE_TIME_THRESHOLD = 600;
  private readonly VERTICAL_SCROLL_THRESHOLD = 25;
  private readonly TAP_MOVEMENT_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 300;

  allCategoryListings: PaginatedUserListings = {
    vehicles: [],
    realEstate: [],
    marketplace: [],
    jobs: [],
    total: 0,
    pagination: {
      vehicles: { page: 1, limit: 20, hasMore: false },
      realEstate: { page: 1, limit: 20, hasMore: false },
      marketplace: { page: 1, limit: 20, hasMore: false },
      jobs: { page: 1, limit: 20, hasMore: false },
    },
  };

  paginatedAllListings: AllListingsPaginated = {
    listings: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };

  allListingsCounts: AllUserListings = {
    vehicles: [],
    realEstate: [],
    marketplace: [],
    jobs: [],
    total: 0,
  };

  sortedListings: ExtendedUserListing[] = [];
  loading = true;
  error: string | null = null;

  viewMode: 'category' | 'all' = 'all';

  currentPage = 1;
  pageSize = 20;

  categoryPages = {
    vehicles: 1,
    realEstate: 1,
    marketplace: 1,
    jobs: 1,
  };

  loadingMore = {
    vehicles: false,
    realEstate: false,
    marketplace: false,
    jobs: false,
  };

  statusFilter: string = 'all';
  statusOptions = [
    { value: 'all', label: 'All Listings' },
    { value: 'active', label: 'Active' },
    { value: 'declined', label: 'Declined' },
    { value: 'pending', label: 'Pending' },
  ];

  messages: {
    success: string | null;
    error: string | null;
  } = {
    success: null,
    error: null,
  };

  constructor(
    private listingsService: ListingsService,
    private authService: AuthService,
    private router: Router
  ) {
    this.refreshSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.performRefresh();
      });
  }

  ngOnInit(): void {
    this.loadUserPreferences();
    this.loadInitialData();
    this.setupAutoRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAutoRefresh(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !this.loading) {
          this.loadInitialData();
        }
      });
    }
  }

  private transformListing(listing: UserListing): ExtendedUserListing {
    let images: string[] = [];

    if (
      listing.images &&
      Array.isArray(listing.images) &&
      listing.images.length > 0
    ) {
      images = listing.images;
    } else if (listing.first_image) {
      images = [listing.first_image];
    } else {
      images = ['/assets/images/listing-placeholder/no-image-placeholder.png'];
    }

    return {
      ...listing,
      images,
      currentImageIndex: 0,
      preloadedImages: new Set([0]),
      isImageLoading: false,
      showCarouselControls: false,
      hasShownControls: false,
      isTouchScrolling: false,
      touchMoved: false,
    };
  }

  loadInitialData(): void {
    this.loading = true;
    this.error = null;

    this.listingsService
      .getAllUserListings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.allListingsCounts = data;

          this.allListingsFlat = [
            ...data.vehicles,
            ...data.realEstate,
            ...data.marketplace,
            ...data.jobs,
          ]
            .map((listing) => this.transformListing(listing))
            .sort(
              (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
            );

          if (this.viewMode === 'all') {
            this.applyFilterAndPagination();
          } else {
            this.loadCategoryListings();
          }

          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading user listings:', error);
          this.error = 'Failed to load your listings. Please try again.';
          this.loading = false;
          this.showErrorMessage(
            'Failed to load listings. Please check your connection and try again.'
          );
        },
      });
  }

  private applyFilterAndPagination(): void {
    let filteredListings = this.allListingsFlat;
    if (this.statusFilter !== 'all') {
      filteredListings = this.allListingsFlat.filter(
        (listing) => listing.status === this.statusFilter
      );
    }

    const totalCount = filteredListings.length;
    const totalPages = Math.ceil(totalCount / this.pageSize) || 1;

    if (this.currentPage > totalPages && totalCount > 0) {
      this.currentPage = 1;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const paginatedListings = filteredListings.slice(startIndex, endIndex);

    this.paginatedAllListings = {
      listings: paginatedListings,
      totalCount,
      currentPage: this.currentPage,
      totalPages,
      hasNextPage: this.currentPage < totalPages && totalCount > 0,
      hasPreviousPage: this.currentPage > 1 && totalCount > 0,
    };

    this.sortedListings = paginatedListings;
  }

  loadCategoryListings(): void {
    this.loading = true;

    this.listingsService
      .getCategoryListingsPaginated({ page: 1, limit: 20 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.allCategoryListings = {
            vehicles: data.vehicles.map((l) => this.transformListing(l)),
            realEstate: data.realEstate.map((l) => this.transformListing(l)),
            marketplace: data.marketplace.map((l) => this.transformListing(l)),
            jobs: data.jobs.map((l) => this.transformListing(l)),
            total: data.total,
            pagination: data.pagination,
          };
          this.categoryPages = {
            vehicles: 1,
            realEstate: 1,
            marketplace: 1,
            jobs: 1,
          };
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading category listings:', error);
          this.error = 'Failed to load your listings. Please try again.';
          this.loading = false;
          this.showErrorMessage(
            'Failed to load listings. Please check your connection and try again.'
          );
        },
      });
  }

  loadMoreListings(category: string): void {
    if (this.loadingMore[category as keyof typeof this.loadingMore]) return;

    const nextPage =
      this.categoryPages[category as keyof typeof this.categoryPages] + 1;
    this.loadingMore[category as keyof typeof this.loadingMore] = true;

    this.listingsService
      .loadMoreUserListings(category, nextPage, 20)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.listings && response.listings.length > 0) {
            const transformedListings = response.listings.map((l) =>
              this.transformListing(l)
            );

            switch (category) {
              case 'vehicles':
                this.allCategoryListings.vehicles = [
                  ...this.allCategoryListings.vehicles,
                  ...transformedListings,
                ];
                this.allCategoryListings.pagination.vehicles.hasMore =
                  response.listings.length === 20;
                break;
              case 'realEstate':
                this.allCategoryListings.realEstate = [
                  ...this.allCategoryListings.realEstate,
                  ...transformedListings,
                ];
                this.allCategoryListings.pagination.realEstate.hasMore =
                  response.listings.length === 20;
                break;
              case 'marketplace':
                this.allCategoryListings.marketplace = [
                  ...this.allCategoryListings.marketplace,
                  ...transformedListings,
                ];
                this.allCategoryListings.pagination.marketplace.hasMore =
                  response.listings.length === 20;
                break;
              case 'jobs':
                this.allCategoryListings.jobs = [
                  ...this.allCategoryListings.jobs,
                  ...transformedListings,
                ];
                this.allCategoryListings.pagination.jobs.hasMore =
                  response.listings.length === 20;
                break;
            }

            this.categoryPages[category as keyof typeof this.categoryPages] =
              nextPage;
            this.allCategoryListings.total =
              this.allCategoryListings.vehicles.length +
              this.allCategoryListings.realEstate.length +
              this.allCategoryListings.marketplace.length +
              this.allCategoryListings.jobs.length;

            if (response.listings.length < 20) {
              switch (category) {
                case 'vehicles':
                  this.allCategoryListings.pagination.vehicles.hasMore = false;
                  break;
                case 'realEstate':
                  this.allCategoryListings.pagination.realEstate.hasMore =
                    false;
                  break;
                case 'marketplace':
                  this.allCategoryListings.pagination.marketplace.hasMore =
                    false;
                  break;
                case 'jobs':
                  this.allCategoryListings.pagination.jobs.hasMore = false;
                  break;
              }
            }
          } else {
            switch (category) {
              case 'vehicles':
                this.allCategoryListings.pagination.vehicles.hasMore = false;
                break;
              case 'realEstate':
                this.allCategoryListings.pagination.realEstate.hasMore = false;
                break;
              case 'marketplace':
                this.allCategoryListings.pagination.marketplace.hasMore = false;
                break;
              case 'jobs':
                this.allCategoryListings.pagination.jobs.hasMore = false;
                break;
            }
          }

          this.loadingMore[category as keyof typeof this.loadingMore] = false;
        },
        error: (error) => {
          console.error(`Error loading more ${category} listings:`, error);
          this.loadingMore[category as keyof typeof this.loadingMore] = false;
          this.showErrorMessage(`Failed to load more ${category} listings.`);
        },
      });
  }

  onTouchStart(event: TouchEvent, listing: ExtendedUserListing): void {
    const touch = event.touches[0];
    listing.touchStartX = touch.clientX;
    listing.touchStartY = touch.clientY;
    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
    listing.touchStartTime = Date.now();
    listing.isTouchScrolling = false;
    listing.touchMoved = false;
  }

  onTouchMove(event: TouchEvent, listing: ExtendedUserListing): void {
    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined
    ) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - listing.touchStartX);
    const deltaY = Math.abs(touch.clientY - listing.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      listing.touchMoved = true;
    }

    if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
      listing.isTouchScrolling = true;
    } else if (deltaX > deltaY && deltaX > 5 && listing.images.length > 1) {
      event.preventDefault();
    }

    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
  }

  onTouchEnd(event: TouchEvent, listing: ExtendedUserListing): void {
    if (listing.images.length <= 1) {
      if (!listing.isTouchScrolling && !listing.touchMoved) {
        this.onViewListing(listing);
      }
      this.resetTouchState(listing);
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined ||
      listing.touchStartTime === undefined
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - listing.touchStartX;
    const deltaY = touch.clientY - listing.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - listing.touchStartTime;

    if (
      listing.isTouchScrolling ||
      (absDeltaY > this.VERTICAL_SCROLL_THRESHOLD && absDeltaY > absDeltaX)
    ) {
      this.resetTouchState(listing);
      return;
    }

    const velocity = deltaTime > 0 ? absDeltaX / deltaTime : 0;
    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const isQuickEnough = deltaTime < this.SWIPE_TIME_THRESHOLD;
    const isFastSwipe =
      velocity >= this.SWIPE_VELOCITY_THRESHOLD &&
      absDeltaX >= this.SWIPE_FAST_THRESHOLD;
    const isLongEnough = absDeltaX > this.SWIPE_THRESHOLD || isFastSwipe;
    const isNotTooVertical = absDeltaY < this.VERTICAL_SCROLL_THRESHOLD;

    if (
      isHorizontalSwipe &&
      isQuickEnough &&
      isLongEnough &&
      isNotTooVertical
    ) {
      event.stopPropagation();
      event.preventDefault();
      if (deltaX > 0) {
        this.previousImage(listing, event);
      } else {
        this.nextImage(listing, event);
      }
    } else if (
      !listing.touchMoved ||
      (absDeltaX <= this.TAP_MOVEMENT_THRESHOLD &&
        absDeltaY <= this.TAP_MOVEMENT_THRESHOLD &&
        deltaTime <= this.TAP_TIME_THRESHOLD)
    ) {
      this.onViewListing(listing);
    }
    this.resetTouchState(listing);
  }

  private resetTouchState(listing: ExtendedUserListing): void {
    setTimeout(() => {
      listing.touchStartX = undefined;
      listing.touchStartY = undefined;
      listing.touchStartTime = undefined;
      listing.lastTouchX = undefined;
      listing.lastTouchY = undefined;
      listing.isTouchScrolling = false;
      listing.touchMoved = false;
    }, 100);
  }

  onImageZoneClick(
    event: Event,
    listing: ExtendedUserListing,
    zone: 'prev' | 'next' | 'view'
  ): void {
    event.stopPropagation();

    if (zone === 'view') {
      this.onViewListing(listing);
    } else if (zone === 'prev') {
      this.previousImage(listing, event);
    } else if (zone === 'next') {
      this.nextImage(listing, event);
    }
  }

  previousImage(listing: ExtendedUserListing, event: Event): void {
    event.stopPropagation();
    if (listing.images.length <= 1) return;
    const newIndex =
      (listing.currentImageIndex - 1 + listing.images.length) %
      listing.images.length;
    this.navigateToImage(listing, newIndex);
  }

  nextImage(listing: ExtendedUserListing, event: Event): void {
    event.stopPropagation();
    if (listing.images.length <= 1) return;
    const newIndex = (listing.currentImageIndex + 1) % listing.images.length;
    this.navigateToImage(listing, newIndex);
  }

  private navigateToImage(
    listing: ExtendedUserListing,
    targetIndex: number
  ): void {
    listing.isImageLoading = true;
    listing.currentImageIndex = targetIndex;
    if (!listing.preloadedImages) {
      listing.preloadedImages = new Set();
    }
    listing.preloadedImages.add(targetIndex);
    this.preloadAdjacentImages(listing, targetIndex);
  }

  private preloadAdjacentImages(
    listing: ExtendedUserListing,
    currentIndex: number
  ): void {
    if (listing.images.length <= 1) return;

    const nextIndex = (currentIndex + 1) % listing.images.length;
    const prevIndex =
      (currentIndex - 1 + listing.images.length) % listing.images.length;

    if (!listing.preloadedImages?.has(nextIndex)) {
      this.preloadImage(listing.images[nextIndex]);
      listing.preloadedImages?.add(nextIndex);
    }

    if (!listing.preloadedImages?.has(prevIndex)) {
      this.preloadImage(listing.images[prevIndex]);
      listing.preloadedImages?.add(prevIndex);
    }
  }

  private preloadImage(url: string): void {
    if (this.loadedImages.has(url)) return;

    const img = new Image();
    img.src = url;
    img.onload = () => {
      this.loadedImages.add(url);
    };
    img.onerror = () => {
      console.warn(`Failed to preload image: ${url}`);
    };
  }

  onListingHover(listing: ExtendedUserListing): void {
    if (listing.images.length <= 1) return;

    listing.images.forEach((imageUrl, index) => {
      if (!listing.preloadedImages?.has(index)) {
        this.preloadImage(imageUrl);
        listing.preloadedImages?.add(index);
      }
    });

    if (!listing.hasShownControls) {
      listing.showCarouselControls = true;
      listing.hasShownControls = true;

      if (listing.controlsTimeout) {
        clearTimeout(listing.controlsTimeout);
      }

      listing.controlsTimeout = setTimeout(() => {
        listing.showCarouselControls = false;
      }, 2000);
    }
  }

  onListingLeave(listing: ExtendedUserListing): void {
    if (listing.controlsTimeout) {
      clearTimeout(listing.controlsTimeout);
    }
    listing.hasShownControls = false;
    listing.showCarouselControls = false;
  }

  hasMultipleImages(listing: ExtendedUserListing): boolean {
    return listing.images.length > 1;
  }

  getImageCounter(listing: ExtendedUserListing): string {
    return `${listing.currentImageIndex + 1}/${listing.images.length}`;
  }

  onImageLoad(imageUrl: string, listing?: ExtendedUserListing): void {
    this.loadedImages.add(imageUrl);
    if (listing) {
      listing.isImageLoading = false;
    }
  }

  private performRefresh(): void {
    this.currentPage = 1;
    this.categoryPages = {
      vehicles: 1,
      realEstate: 1,
      marketplace: 1,
      jobs: 1,
    };
    this.loadInitialData();
  }

  goToPage(page: number): void {
    if (
      page >= 1 &&
      page <= this.paginatedAllListings.totalPages &&
      page !== this.currentPage
    ) {
      this.currentPage = page;
      this.applyFilterAndPagination();
    }
  }

  goToPreviousPage(): void {
    if (this.paginatedAllListings.hasPreviousPage) {
      this.currentPage = this.currentPage - 1;
      this.applyFilterAndPagination();
    }
  }

  goToNextPage(): void {
    if (this.paginatedAllListings.hasNextPage) {
      this.currentPage = this.currentPage + 1;
      this.applyFilterAndPagination();
    }
  }

  getVisiblePageNumbers(): number[] {
    const totalPages = this.paginatedAllListings.totalPages;
    if (totalPages <= 1) {
      return [1];
    }

    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, this.currentPage - delta);
      i <= Math.min(totalPages - 1, this.currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (this.currentPage - delta > 2) {
      rangeWithDots.push(1, -1);
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (this.currentPage + delta < totalPages - 1) {
      rangeWithDots.push(-1, totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  }

  hasMoreListings(category: string): boolean {
    const pagination =
      this.allCategoryListings.pagination[
        category as keyof typeof this.allCategoryListings.pagination
      ];
    return pagination.hasMore;
  }

  isLoadingMore(category: string): boolean {
    return this.loadingMore[category as keyof typeof this.loadingMore];
  }

  getCategoryListingCount(category: string): number {
    const categoryListings =
      this.allCategoryListings[
        category as keyof typeof this.allCategoryListings
      ];
    return Array.isArray(categoryListings) ? categoryListings.length : 0;
  }

  setViewMode(mode: 'category' | 'all'): void {
    this.viewMode = mode;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('listings-view-mode', mode);
    }

    if (mode === 'all') {
      this.currentPage = 1;
      this.applyFilterAndPagination();
    } else {
      this.loadCategoryListings();
    }
  }

  onStatusFilterChange(status: string): void {
    this.statusFilter = status;

    if (this.viewMode === 'all') {
      this.currentPage = 1;
      this.applyFilterAndPagination();
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('listings-status-filter', status);
    }
  }

  getCategoryName(tableName: string): string {
    return this.listingsService.getCategoryFromTableName(tableName);
  }

  getSubcategoryName(tableName: string): string {
    return this.listingsService.getSubcategoryFromTableName(tableName);
  }

  formatPrice(price: number, currency: string): string {
    if (price === 0) {
      return 'Free';
    }

    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      return formatter.format(price);
    } catch (error) {
      return `${price} ${currency || 'EUR'}`;
    }
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor(
        (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays === 0) {
        return 'Today';
      } else if (diffInDays === 1) {
        return 'Yesterday';
      } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    } catch (error) {
      return 'Unknown date';
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-active';
      case 'declined':
        return 'status-declined';
      case 'pending':
        return 'status-pending';
      case 'deleted':
        return 'status-deleted';
      default:
        return 'status-default';
    }
  }

  onRefresh(): void {
    this.refreshSubject.next();
  }

  onViewListing(listing: ExtendedUserListing): void {
    if (listing.isTouchScrolling || listing.touchMoved) {
      return;
    }

    if (listing.touchStartTime !== undefined) {
      const tapDuration = Date.now() - listing.touchStartTime;
      if (tapDuration > this.TAP_TIME_THRESHOLD) {
        return;
      }
    }

    try {
      const subcategory = this.listingsService.getSubcategoryFromTableName(
        listing.table_name
      );
      this.router.navigate(['/listing-details', subcategory, listing.id]);
    } catch (error) {
      console.error('Error getting subcategory:', error);
      this.router.navigate([
        '/listing-details',
        listing.table_name,
        listing.id,
      ]);
    }
  }

  onCardClick(listing: ExtendedUserListing, event: Event): void {
    const target = event.target as HTMLElement;

    if (
      target.closest('.listing-actions') ||
      target.closest('.action-btn') ||
      target.closest('button')
    ) {
      return;
    }

    this.onViewListing(listing);
  }

  private showSuccessMessage(message: string): void {
    this.messages.success = message;
    this.messages.error = null;

    setTimeout(() => {
      this.messages.success = null;
    }, 5000);
  }

  private showErrorMessage(message: string): void {
    this.messages.error = message;
    this.messages.success = null;

    setTimeout(() => {
      this.messages.error = null;
    }, 8000);
  }

  private clearMessages(): void {
    this.messages.success = null;
    this.messages.error = null;
  }

  dismissMessage(type: 'success' | 'error'): void {
    this.messages[type] = null;
  }

  trackByListingId(index: number, listing: ExtendedUserListing): number {
    return listing.id;
  }

  getListingImage(listing: ExtendedUserListing): string {
    return listing.images[listing.currentImageIndex];
  }

  getCategoryCounts(): { [key: string]: number } {
    return {
      vehicles: this.allListingsCounts.vehicles.length,
      realEstate: this.allListingsCounts.realEstate.length,
      marketplace: this.allListingsCounts.marketplace.length,
      jobs: this.allListingsCounts.jobs.length,
    };
  }

  private loadUserPreferences(): void {
    if (typeof localStorage !== 'undefined') {
      const savedViewMode = localStorage.getItem('listings-view-mode') as
        | 'category'
        | 'all';
      const savedStatusFilter = localStorage.getItem('listings-status-filter');

      if (savedViewMode) {
        this.viewMode = savedViewMode;
      }

      if (savedStatusFilter) {
        this.statusFilter = savedStatusFilter;
      }
    }
  }

  getStatusBadgeIcon(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return '●';
      case 'declined':
        return '○';
      case 'pending':
        return '◐';
      case 'deleted':
        return '×';
      default:
        return '?';
    }
  }

  getCategoryIcon(tableName: string): string {
    const category = this.getCategoryName(tableName).toLowerCase();
    switch (category) {
      case 'vehicles':
        return '🚗';
      case 'real estate':
        return '🏠';
      case 'marketplace':
        return '🛒';
      case 'jobs':
        return '💼';
      default:
        return '📦';
    }
  }

  getTotalListings(): number {
    return this.allListingsCounts.total;
  }

  getCurrentViewTotal(): number {
    if (this.viewMode === 'all') {
      return this.paginatedAllListings.totalCount;
    } else {
      return this.allCategoryListings.total;
    }
  }
}
